import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import { Upload } from 'lucide-react'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { SensitiveFileInput as LegacySensitiveFileViewer } from 'browser/components/atomic-elements/atoms/sensitive-file-input/sensitive-file-input'
import apis from 'browser/app/models/apis'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import {
  defaultDeniedConfig,
  defaultProtectedConfig,
  defaultReasonConfig,
} from 'shared-libs/components/types/sensitive-file'

import { UI2SensitiveFileFieldProps } from 'shared-libs/components/ui2/fields/ui2-sensitive-file-field/ui2-sensitive-file-field-props'
import { UI2FieldShell, IUI2FieldShellProps } from '../field-ui/field-shell/field-shell'
import { UI2FieldControl } from '../field-ui/field-control/field-control'

import './_ui2-sensitive-file-field.scss'

interface IUploadcareAuth {
  signature: string
  expire: number
  publicKey: string
}

interface IUI2SensitiveFileFieldWebExtras extends Omit<IBaseProps, 'children'> {
  onChange?: (value: any) => void
  frames?: FramesManager
}

export interface IUI2SensitiveFileFieldProps
  extends UI2SensitiveFileFieldProps,
    IUI2SensitiveFileFieldWebExtras {}

interface IUI2SensitiveFileFieldState {
  isUploading: boolean
  uploadError: string | null
}

export class UI2SensitiveFileField extends React.Component<
  IUI2SensitiveFileFieldProps,
  IUI2SensitiveFileFieldState
> {
  public static defaultProps: Partial<IUI2SensitiveFileFieldProps> = {
    buttonText: 'Upload sensitive file',
    successText: 'Sensitive file uploaded',
    provider: 'uploadcare',
    retentionAge: '1y',
    emptyText: 'No value available',
    protectedCfg: defaultProtectedConfig,
    reasonCfg: defaultReasonConfig,
    deniedCfg: defaultDeniedConfig,
    canRequestAccess: true,
  }

  private fileInputRef = React.createRef<HTMLInputElement>()

  public state: IUI2SensitiveFileFieldState = {
    isUploading: false,
    uploadError: null,
  }

  public render() {
    const { className, style, value } = this.props
    const isEmpty = UI2SensitiveFileField.isValueEmpty(value)
    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps<any>)}
        isValueEmpty={UI2SensitiveFileField.isValueEmpty}
        className={classNames('ui2-sensitiveFileField', className, {
          'ui2-sensitiveFileField--isEmpty': isEmpty,
        })}
        style={style}
        renderReadOnly={this.renderReadOnly}
        // Body is a UI2FieldControl, which slots Cancel beside the input.
        bodyIsFieldControl={true}
      >
        <UI2FieldControl>{this.renderEditableBody()}</UI2FieldControl>
      </UI2FieldShell>
    )
  }

  private static isValueEmpty = (value: any): boolean => {
    return (
      _.isEmpty(value?.storage?.fileId) ||
      _.isEmpty(value?.storage?.provider) ||
      _.isEmpty(value?.storage?.retentionAge)
    )
  }

  // Edit is the shell's job now — the local pill sat in a column wrapper,
  // which is what pushed it below the viewer instead of inline with it.
  private renderReadOnly = (value: any, { editNode }: { editNode?: React.ReactNode }): React.ReactNode => {
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>
          <div className='ui2-sensitiveFileField-readOnly'>{this.renderViewer()}</div>
        </div>
        {editNode}
      </>
    )
  }

  private renderEditableBody(): React.ReactNode {
    const { value, isDisabled } = this.props
    const hasValue = !UI2SensitiveFileField.isValueEmpty(value)

    if (hasValue) return this.renderViewer()
    return this.renderEmptyStateUpload(!!isDisabled)
  }

  private renderViewer(): React.ReactNode {
    const { value, frames, protectedCfg, reasonCfg, deniedCfg, emptyText, canRequestAccess } = this.props
    return (
      <div
        className={classNames('ui2-sensitiveFileField-viewerHost', {
          // The viewer is a legacy component with handlers of its own, so
          // the host blocks them rather than threading a disabled flag
          // through it.
          'ui2-sensitiveFileField-viewerHost--disabled': this.props.isDisabled,
        })}
      >
        <LegacySensitiveFileViewer
          label={''}
          emptyText={emptyText}
          value={value}
          frames={frames}
          protectedCfg={protectedCfg}
          reasonCfg={reasonCfg}
          deniedCfg={deniedCfg}
          canRequestAccess={canRequestAccess}
        />
      </div>
    )
  }

  private renderEmptyStateUpload(isDisabled: boolean): React.ReactNode {
    const { buttonText } = this.props
    const { isUploading, uploadError } = this.state
    return (
      <>
        <input
          ref={this.fileInputRef}
          type='file'
          accept='image/*,application/pdf'
          className='ui2-sensitiveFileField-hiddenInput'
          onChange={this.handleFileChosen}
          disabled={isDisabled || isUploading}
        />
        <button
          type='button'
          className='ui2-sensitiveFileField-launcher'
          onClick={this.handleUploadClick}
          disabled={isDisabled || isUploading}
        >
          <Upload size={16} />
          <span>{isUploading ? 'Uploading…' : buttonText}</span>
        </button>
        {!!uploadError && (
          <div className='ui2-sensitiveFileField-uploadError'>{uploadError}</div>
        )}
      </>
    )
  }

  private handleUploadClick = () => {
    this.fileInputRef.current?.click()
  }

  private handleFileChosen = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (event.target) event.target.value = ''
    if (!file) return
    await this.uploadFile(file)
  }

  private uploadFile = async (file: File) => {
    const { provider, retentionAge, onChange, frames } = this.props
    this.setState({ isUploading: true, uploadError: null })
    try {
      const auth = (await apis.requestSensitiveFileUploadCredentials({
        provider: provider!,
        retentionAge: retentionAge!,
      })) as IUploadcareAuth
      const formData = new FormData()
      formData.append('UPLOADCARE_PUB_KEY', auth.publicKey)
      formData.append('UPLOADCARE_STORE', '1')
      formData.append('signature', auth.signature)
      formData.append('expire', String(auth.expire))
      formData.append('file', file, file.name)
      const response = await fetch('https://upload.uploadcare.com/base/', {
        method: 'POST',
        body: formData,
      })
      if (!response.ok) {
        const text = await response.text().catch(() => '')
        throw new Error(text || `Upload failed with status ${response.status}`)
      }
      const payload = await response.json()
      const fileId: string | undefined = payload?.file
      if (!fileId) throw new Error('Upload succeeded but no file id was returned')

      const user = frames?.getValue?.('currentUser')
      const sensitiveFile = {
        name: file.name,
        fileType: file.type?.includes('pdf') ? 'pdf' : 'image',
        createdBy: user
          ? { displayName: user.displayName, entityId: user.uniqueId }
          : undefined,
        creationDate: new Date().toISOString(),
        storage: {
          provider: provider!,
          retentionAge: retentionAge!,
          fileId,
          mimeType: file.type,
        },
      }
      onChange?.(sensitiveFile)
    } catch (err: any) {
      const message = err?.message || 'Upload failed. Please try again.'
      this.setState({ uploadError: message })
    } finally {
      this.setState({ isUploading: false })
    }
  }
}
